import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import usMap from '../data/usStateMap.json';
import './nexus-map.css';

interface NexusState {
    state_code: string;
    state_name: string;
    sales_threshold: number;
    current_sales: number;
    transaction_threshold?: number;
    current_transactions: number;
    percent_to_sales_threshold: number;
    percent_to_transaction_threshold?: number;
    exceeded_threshold: boolean;
    approaching_threshold: boolean;
    nexus_status: 'exceeded' | 'approaching' | 'safe';
    effective_date: string;
    lookback_period: string;
    current_period?: string;
    rule_type?: string;
    nexus_rule_triggered?: string;
    rule_explanation?: string;
}

interface NexusMapViewProps {
    nexusData: NexusState[];
    companyId?: number;
    filingPeriod?: string;
}

const statusLabels = { exceeded: 'Exceeded', approaching: 'Approaching', safe: 'Under threshold', none: 'No data' };
const money = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
const percent = (value: number) => value > 0 && value < 0.1 ? '<0.1%' : `${Number.isFinite(value) ? Number(value.toFixed(1)) : 0}%`;
const effectiveDate = (value: string) => {
    const date = new Date(`${value.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const NexusMapView: React.FC<NexusMapViewProps> = ({ nexusData, companyId, filingPeriod }) => {
    const [params, setParams] = useSearchParams();
    const statesByCode = useMemo(() => new Map(nexusData.map(state => [state.state_code.toUpperCase(), state])), [nexusData]);
    const priorityState = useMemo(() => [...nexusData].sort((a, b) => {
        const rank = { exceeded: 2, approaching: 1, safe: 0 };
        const progress = (s: NexusState) => Math.max(s.percent_to_sales_threshold || 0, s.percent_to_transaction_threshold || 0);
        return rank[b.nexus_status] - rank[a.nexus_status] || progress(b) - progress(a) || a.state_name.localeCompare(b.state_name);
    }).find(state => usMap.states.some(shape => shape.code === state.state_code)), [nexusData]);
    const selectedCode = params.get('state')?.toUpperCase();
    const selectedShape = usMap.states.find(state => state.code === selectedCode)
        || usMap.states.find(state => state.code === priorityState?.state_code) || usMap.states[0];
    const state = statesByCode.get(selectedShape.code);
    const status = state?.nexus_status || 'none';
    const selectState = (code: string) => {
        const next = new URLSearchParams(params);
        next.set('state', code);
        setParams(next, { replace: true, preventScrollReset: true });
    };
    const insightsParams = new URLSearchParams({ state: selectedShape.code });
    if (filingPeriod) insightsParams.set('filing_period', filingPeriod);
    const salesPercent = state && state.sales_threshold > 0 ? state.current_sales / state.sales_threshold * 100 : state?.percent_to_sales_threshold || 0;
    const transactionPercent = state?.transaction_threshold ? state.current_transactions / state.transaction_threshold * 100 : state?.percent_to_transaction_threshold;
    const progress = Math.min(100, Math.max(0, salesPercent));

    return (
        <section className="gt-nexus-map" aria-labelledby="nexus-map-title">
            <div className="gt-nexus-map-head">
                <div>
                    <h2 id="nexus-map-title">State nexus status</h2>
                    <p>Select a state to explore its sales and thresholds.</p>
                </div>
                <ul className="gt-nexus-legend" aria-label="Map legend">
                    {(Object.keys(statusLabels) as Array<keyof typeof statusLabels>).map(key => (
                        <li key={key}><span className={`gt-nexus-swatch gt-nexus-swatch--${key}`} aria-hidden="true" />{statusLabels[key]}</li>
                    ))}
                </ul>
            </div>
            <div className="gt-nexus-map-layout">
                <figure className="gt-nexus-geography">
                    <svg viewBox={usMap.viewBox} role="group" aria-label="U.S. economic nexus map">
                        <desc>All fifty states and Washington, D.C. Select a state with a click, Enter, or Space. Arrow keys move between states. Alaska and Hawaii are shown as insets.</desc>
                        {usMap.states.map(shape => {
                            const report = statesByCode.get(shape.code);
                            const mapStatus = report?.nexus_status || 'none';
                            const selected = selectedShape.code === shape.code;
                            const callout = shape.anchor[0] !== shape.label[0] || shape.anchor[1] !== shape.label[1];
                            return (
                                <g key={shape.code} className={`gt-nexus-state gt-nexus-state--${mapStatus}${selected ? ' is-selected' : ''}`}
                                    role="button" tabIndex={selected ? 0 : -1} aria-pressed={selected} data-state-code={shape.code}
                                    aria-label={`${shape.name}: ${statusLabels[mapStatus]}`}
                                    onClick={() => selectState(shape.code)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); selectState(shape.code); }
                                        if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(event.key)) {
                                            event.preventDefault();
                                            const step = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
                                            const index = usMap.states.findIndex(item => item.code === shape.code);
                                            const next = usMap.states[(index + step + usMap.states.length) % usMap.states.length];
                                            selectState(next.code);
                                            event.currentTarget.ownerSVGElement?.querySelector<SVGGElement>(`[data-state-code="${next.code}"]`)?.focus();
                                        }
                                    }}>
                                    <path d={shape.path} fillRule="evenodd" vectorEffect="non-scaling-stroke" />
                                    {callout && <line x1={shape.anchor[0]} y1={shape.anchor[1]} x2={shape.label[0] - 12} y2={shape.label[1] - 6} />}
                                    <text x={shape.label[0]} y={shape.label[1]} textAnchor={callout ? 'start' : 'middle'}>{shape.code}</text>
                                </g>
                            );
                        })}
                    </svg>
                    <figcaption>
                        <span>Alaska and Hawaii are repositioned and not to scale.</span>
                        <a href={usMap.source} target="_blank" rel="noopener noreferrer">U.S. Census Bureau boundaries</a>
                    </figcaption>
                </figure>
                <aside className="gt-nexus-state-detail" aria-label="State nexus details">
                    <label className="gt-nexus-state-picker">State
                        <select value={selectedShape.code} onChange={event => selectState(event.target.value)}>
                            {usMap.states.map(shape => <option key={shape.code} value={shape.code}>{shape.name}</option>)}
                        </select>
                    </label>
                    <div className="gt-nexus-detail-content">
                        <div className="gt-nexus-state-heading" aria-live="polite" aria-atomic="true">
                            <h3>{selectedShape.name}</h3>
                            <span className={`gt-nexus-status gt-nexus-status--${status}`}>{statusLabels[status]}</span>
                        </div>
                        {state ? <>
                            <dl className="gt-nexus-sales">
                                <dt>Sales in this range</dt><dd>{money(state.current_sales)}</dd>
                            </dl>
                            <div className={`gt-nexus-progress gt-nexus-progress--${status}`} role="progressbar" aria-label="Sales threshold progress"
                                aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}
                                aria-valuetext={`${percent(salesPercent)} of sales threshold`}>
                                <span style={{ transform: `scaleX(${progress / 100})` }} />
                            </div>
                            <p className="gt-nexus-progress-caption">{percent(salesPercent)} of sales threshold</p>
                            <dl className="gt-nexus-facts">
                                <div><dt>Sales threshold</dt><dd>{money(state.sales_threshold)}</dd></div>
                                <div><dt>Transactions</dt><dd>{state.current_transactions.toLocaleString()}</dd></div>
                                <div><dt>Transaction threshold</dt><dd>{state.transaction_threshold ? state.transaction_threshold.toLocaleString() : 'Not applicable'}</dd></div>
                                {state.transaction_threshold && transactionPercent != null ? <div><dt>Transaction progress</dt><dd>{percent(transactionPercent!)}</dd></div> : null}
                                {state.effective_date && <div><dt>Threshold effective</dt><dd>{effectiveDate(state.effective_date)}</dd></div>}
                                {state.lookback_period && <div><dt>Lookback</dt><dd>{state.lookback_period}</dd></div>}
                            </dl>
                            {state.rule_explanation && <p className="gt-nexus-rule">{state.rule_explanation}</p>}
                            {state.rule_type && <p className="gt-nexus-rule-type">{state.rule_type === 'OR' ? 'Either threshold applies.' : state.rule_type === 'AND' ? 'Both thresholds are required.' : state.rule_type === 'SALES_ONLY' ? 'Sales threshold applies.' : `Rule: ${state.rule_type}`}</p>}
                            {companyId && <Link className="gt-nexus-insights-link" to={`/company/${companyId}/transaction-insights?${insightsParams.toString()}`}>View state transactions</Link>}
                        </> : <p className="gt-nexus-no-data">No nexus data was returned for this state in the selected range. It may be outside the report’s activity filter.</p>}
                    </div>
                </aside>
            </div>
        </section>
    );
};

export default NexusMapView;
